import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

const API = '/api';

function authHeaders() {
  const token = localStorage.getItem('adminToken');
  return { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
}

function getUser() {
  try { return JSON.parse(localStorage.getItem('adminUser')); } catch { return null; }
}

// ============================== MAIN DASHBOARD ==============================
export default function AdminDashboard() {
  const navigate = useNavigate();
  const [user] = useState(getUser);
  const [view, setView] = useState('dashboard'); // dashboard | jobs | newjob | editjob | settings-users | settings-logs
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [stats, setStats] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [jobsTotal, setJobsTotal] = useState(0);
  const [jobsPage, setJobsPage] = useState(1);
  const [editingJob, setEditingJob] = useState(null);
  const [logs, setLogs] = useState([]);
  const [logsTotal, setLogsTotal] = useState(0);
  const [logsPage, setLogsPage] = useState(1);
  const [users, setUsers] = useState([]);
  const [usersTotal, setUsersTotal] = useState(0);
  const [usersPage, setUsersPage] = useState(1);
  const [userStats, setUserStats] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!localStorage.getItem('adminToken')) { navigate('/admin/login'); return; }
    if (!user || !['admin', 'operator'].includes(user.role)) { navigate('/admin/login'); return; }
  }, [user, navigate]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await fetch(`${API}/admin/stats`, { headers: authHeaders() });
      if (res.status === 401) { logout(); return; }
      const data = await res.json();
      setStats(data.stats);
    } catch (e) { console.error(e); }
  }, []);

  const fetchJobs = useCallback(async () => {
    try {
      let url = `${API}/admin/jobs?page=${jobsPage}&limit=20`;
      if (statusFilter) url += `&status=${statusFilter}`;
      if (searchQuery) url += `&search=${encodeURIComponent(searchQuery)}`;
      const res = await fetch(url, { headers: authHeaders() });
      if (res.status === 401) { logout(); return; }
      const data = await res.json();
      setJobs(data.jobs || []);
      setJobsTotal(data.total || 0);
    } catch (e) { console.error(e); }
  }, [jobsPage, statusFilter, searchQuery]);

  const fetchLogs = useCallback(async () => {
    try {
      const res = await fetch(`${API}/admin/audit-logs?page=${logsPage}&limit=30`, { headers: authHeaders() });
      if (res.ok) { const data = await res.json(); setLogs(data.logs || []); setLogsTotal(data.total || 0); }
    } catch (e) { console.error(e); }
  }, [logsPage]);

  const fetchUsers = useCallback(async () => {
    try {
      const res = await fetch(`${API}/admin/users?page=${usersPage}&limit=30`, { headers: authHeaders() });
      if (res.ok) { const data = await res.json(); setUsers(data.users || []); setUsersTotal(data.total || 0); }
    } catch (e) { console.error(e); }
  }, [usersPage]);

  const fetchUserStats = useCallback(async () => {
    try {
      const res = await fetch(`${API}/admin/user-stats`, { headers: authHeaders() });
      if (res.ok) { const data = await res.json(); setUserStats(data.stats); }
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { if (view === 'dashboard') fetchStats(); }, [view, fetchStats]);
  useEffect(() => { if (view === 'jobs') fetchJobs(); }, [view, fetchJobs]);
  useEffect(() => { if (view === 'settings-logs') fetchLogs(); }, [view, fetchLogs]);
  useEffect(() => { if (view === 'settings-users') { fetchUsers(); fetchUserStats(); } }, [view, fetchUsers, fetchUserStats]);
  useEffect(() => { if (view.startsWith('settings')) setSettingsOpen(true); }, [view]);

  const logout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    navigate('/admin/login');
  };

  const flash = (msg) => { setMessage(msg); setTimeout(() => setMessage(''), 3000); };

  // ---- Job actions ----
  const deleteJob = async (id, title) => {
    if (!window.confirm(`Delete "${title}"?`)) return;
    const res = await fetch(`${API}/jobs/${id}`, { method: 'DELETE', headers: authHeaders() });
    if (res.ok) { flash('Job deleted'); fetchJobs(); fetchStats(); } else { flash('Failed to delete'); }
  };

  const toggleJobStatus = async (id, currentStatus) => {
    const newStatus = currentStatus === 'active' ? 'closed' : 'active';
    const res = await fetch(`${API}/jobs/${id}`, {
      method: 'PUT', headers: authHeaders(), body: JSON.stringify({ status: newStatus }),
    });
    if (res.ok) { flash(`Job ${newStatus === 'active' ? 'published' : 'closed'}`); fetchJobs(); fetchStats(); }
  };

  // ---- Operator actions ----
  
  return (
    <div style={s.layout}>
      {/* Sidebar */}
      <aside style={s.sidebar}>
        <div style={s.sidebarHeader}>
          <div style={s.logo}>H</div>
          <div>
            <div style={s.brandName}>Horizon Admin</div>
            <div style={s.brandSub}>{user.role.toUpperCase()}</div>
          </div>
        </div>
        <nav style={s.nav}>
          <button onClick={() => { setView('dashboard'); setMessage(''); }}
            style={{ ...s.navBtn, ...(view === 'dashboard' ? s.navBtnActive : {}) }}>
            📊 Dashboard
          </button>
          <button onClick={() => { setView('jobs'); setMessage(''); }}
            style={{ ...s.navBtn, ...(view === 'jobs' ? s.navBtnActive : {}) }}>
            📋 Jobs
          </button>
          <button onClick={() => { setView('newjob'); setMessage(''); }}
            style={{ ...s.navBtn, ...(view === 'newjob' ? s.navBtnActive : {}) }}>
            ➕ Publish Job
          </button>
          
          {user.role === 'admin' && (
            <>
              <button onClick={() => setSettingsOpen(!settingsOpen)}
                style={{ ...s.navBtn, ...(view.startsWith('settings') ? s.navBtnActive : {}) }}>
                <span style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>⚙️ Settings</span>
                  <span style={{ fontSize: '10px', transition: 'transform 0.2s', transform: settingsOpen ? 'rotate(90deg)' : 'rotate(0deg)' }}>▶</span>
                </span>
              </button>
              {settingsOpen && (
                <div style={s.submenu}>
                  <button onClick={() => { setView('settings-users'); setMessage(''); }}
                    style={{ ...s.submenuBtn, ...(view === 'settings-users' ? s.submenuBtnActive : {}) }}>
                    👥 User Management
                  </button>
                  <button onClick={() => { setView('settings-logs'); setMessage(''); }}
                    style={{ ...s.submenuBtn, ...(view === 'settings-logs' ? s.submenuBtnActive : {}) }}>
                    📝 Audit Logs
                  </button>
                </div>
              )}
            </>
          )}
        </nav>
        <div style={s.sidebarFooter}>
          <div style={{ fontSize: '13px', color: '#94a3b8', marginBottom: '10px' }}>
            {user.firstName} {user.lastName}
          </div>
          <button onClick={logout} style={s.logoutBtn}>Sign Out</button>
        </div>
      </aside>

      {/* Main Content */}
      <main style={s.main}>
        {message && <div style={s.flash}>{message}</div>}

        {view === 'dashboard' && <DashboardView stats={stats} setView={setView} />}
        {view === 'jobs' && (
          <JobsListView jobs={jobs} total={jobsTotal} page={jobsPage} setPage={setJobsPage}
            statusFilter={statusFilter} setStatusFilter={setStatusFilter}
            searchQuery={searchQuery} setSearchQuery={setSearchQuery}
            onDelete={deleteJob} onToggle={toggleJobStatus}
            onEdit={(job) => { setEditingJob(job); setView('editjob'); }}
            fetchJobs={fetchJobs} />
        )}
        {view === 'newjob' && <JobForm onSave={() => { flash('Job published!'); setView('jobs'); fetchJobs(); }} />}
        {view === 'editjob' && editingJob && (
          <JobForm job={editingJob} onSave={() => { flash('Job updated!'); setView('jobs'); fetchJobs(); }}
            onCancel={() => setView('jobs')} />
        )}
        {view === 'settings-logs' && user.role === 'admin' && (
          <AuditLogsView logs={logs} total={logsTotal} page={logsPage} setPage={setLogsPage} />
        )}
        {view === 'settings-users' && user.role === 'admin' && (
          <SettingsView users={users} total={usersTotal} page={usersPage} setPage={setUsersPage}
            userStats={userStats} onRefresh={() => { fetchUsers(); fetchUserStats(); }} flash={flash} />
        )}
      </main>
    </div>
  );
}

// ============================== DASHBOARD VIEW ==============================
function DashboardView({ stats, setView }) {
  if (!stats) return <p>Loading...</p>;
  const cards = [
    { label: 'Active Jobs', value: stats.activeJobs || 0, color: '#28a745' },
    { label: 'Draft Jobs', value: stats.draftJobs || 0, color: '#ffc107' },
    { label: 'Closed Jobs', value: stats.closedJobs || 0, color: '#6c757d' },
    { label: 'Featured', value: stats.featuredJobs || 0, color: '#ff6b35' },
    { label: 'Temporary', value: stats.tempJobs || 0, color: '#17a2b8' },
    { label: 'Permanent', value: stats.permJobs || 0, color: '#1a2f4d' },
    { label: 'Contract', value: stats.contractJobs || 0, color: '#6f42c1' },
    { label: 'Total Jobs', value: stats.totalJobs || 0, color: '#343a40' },
  ];
  return (
    <div>
      <h2 style={s.pageTitle}>Dashboard</h2>
      <div style={s.statsGrid}>
        {cards.map((c, i) => (
          <div key={i} style={{ ...s.statCard, borderTopColor: c.color }}>
            <div style={{ fontSize: '32px', fontWeight: 800, color: c.color }}>{c.value}</div>
            <div style={{ fontSize: '14px', color: '#6c757d', marginTop: '5px' }}>{c.label}</div>
          </div>
        ))}
      </div>
      <div style={{ marginTop: '30px' }}>
        <button onClick={() => setView('newjob')} style={s.primaryBtn}>➕ Publish New Job</button>
      </div>
    </div>
  );
}

// ============================== JOBS LIST VIEW ==============================
function JobsListView({ jobs, total, page, setPage, statusFilter, setStatusFilter,
  searchQuery, setSearchQuery, onDelete, onToggle, onEdit }) {
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
        <h2 style={s.pageTitle}>Jobs ({total})</h2>
      </div>
      <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', flexWrap: 'wrap' }}>
        <input placeholder="Search jobs..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
          style={{ ...s.input, flex: 1, minWidth: '200px' }} />
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={{ ...s.input, width: '160px' }}>
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="draft">Draft</option>
          <option value="closed">Closed</option>
        </select>
      </div>
      <div style={s.table}>
        <div style={s.tableHeader}>
          <span style={{ flex: 2 }}>Title</span>
          <span style={{ flex: 1 }}>Location</span>
          <span style={{ flex: 1 }}>Type</span>
          <span style={{ flex: 1 }}>Status</span>
          <span style={{ flex: 1 }}>Posted</span>
          <span style={{ flex: 1.5, textAlign: 'right' }}>Actions</span>
        </div>
        {jobs.length === 0 && <div style={{ padding: '20px', color: '#6c757d', textAlign: 'center' }}>No jobs found</div>}
        {jobs.map(job => (
          <div key={job.id} style={s.tableRow}>
            <span style={{ flex: 2, fontWeight: 600 }}>
              {job.title}
              {job.isFeatured ? <span style={s.featuredBadge}>★</span> : null}
            </span>
            <span style={{ flex: 1, color: '#6c757d' }}>{job.location || '—'}</span>
            <span style={{ flex: 1 }}>
              <span style={{ ...s.typeBadge, background: job.jobType === 'temporary' ? '#17a2b8' : job.jobType === 'contract' ? '#ffc107' : '#28a745' }}>
                {job.jobType || 'permanent'}
              </span>
            </span>
            <span style={{ flex: 1 }}>
              <span style={{ ...s.statusBadge, background: job.status === 'active' ? '#d4edda' : job.status === 'draft' ? '#fff3cd' : '#e2e3e5',
                color: job.status === 'active' ? '#155724' : job.status === 'draft' ? '#856404' : '#383d41' }}>
                {job.status}
              </span>
            </span>
            <span style={{ flex: 1, color: '#6c757d', fontSize: '13px' }}>
              {new Date(job.createdAt).toLocaleDateString('en-GB')}
            </span>
            <span style={{ flex: 1.5, textAlign: 'right', display: 'flex', gap: '5px', justifyContent: 'flex-end' }}>
              <button onClick={() => onEdit(job)} style={s.smallBtn} title="Edit">✏️</button>
              <button onClick={() => onToggle(job.id, job.status)} style={s.smallBtn}
                title={job.status === 'active' ? 'Close' : 'Publish'}>
                {job.status === 'active' ? '⏸️' : '▶️'}
              </button>
              <button onClick={() => onDelete(job.id, job.title)} style={{ ...s.smallBtn, background: '#fee' }} title="Delete">🗑️</button>
            </span>
          </div>
        ))}
      </div>
      {total > 20 && (
        <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', marginTop: '20px' }}>
          <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} style={s.pageBtn}>← Prev</button>
          <span style={{ padding: '8px 15px', color: '#6c757d' }}>Page {page} of {Math.ceil(total / 20)}</span>
          <button disabled={page >= Math.ceil(total / 20)} onClick={() => setPage(p => p + 1)} style={s.pageBtn}>Next →</button>
        </div>
      )}
    </div>
  );
}

// ============================== JOB FORM ==============================
function JobForm({ job, onSave, onCancel }) {
  const [form, setForm] = useState({
    title: job?.title || '', description: job?.description || '', location: job?.location || '',
    salary: job?.salary || '', salaryMin: job?.salaryMin || '', salaryMax: job?.salaryMax || '',
    salaryType: job?.salaryType || 'annual',
    jobType: job?.jobType || 'permanent', sector: job?.sector || '', trade: job?.trade || '',
    experienceLevel: job?.experienceLevel || '', cscsRequired: job?.cscsRequired || false,
    qualifications: job?.qualifications || '', startDate: job?.startDate ? job.startDate.split('T')[0] : '',
    duration: job?.duration || '', companyName: job?.companyName || '',
    status: job?.status || 'active', isFeatured: job?.isFeatured || false,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(f => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.description || !form.location) {
      setError('Title, description, and location are required'); return;
    }
    setSaving(true); setError('');
    try {
      const url = job ? `${API}/jobs/${job.id}` : `${API}/jobs`;
      const method = job ? 'PUT' : 'POST';
      const body = { ...form };
      if (body.salaryMin) body.salaryMin = parseFloat(body.salaryMin);
      if (body.salaryMax) body.salaryMax = parseFloat(body.salaryMax);
      body.cscsRequired = body.cscsRequired ? 1 : 0;
      body.isFeatured = body.isFeatured ? 1 : 0;

      const res = await fetch(url, { method, headers: authHeaders(), body: JSON.stringify(body) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to save');
      onSave();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2 style={s.pageTitle}>{job ? 'Edit Job' : 'Publish New Job'}</h2>
        {onCancel && <button onClick={onCancel} style={s.secondaryBtn}>← Back to Jobs</button>}
      </div>
      {error && <div style={{ ...s.flash, background: '#fee', color: '#d33' }}>{error}</div>}
      <form onSubmit={handleSubmit} style={s.formCard}>
        <div style={s.formGrid}>
          <div style={s.formGroup}>
            <label style={s.formLabel}>Job Title *</label>
            <input name="title" value={form.title} onChange={handleChange} style={s.input}
              placeholder="e.g. Site Manager" required />
          </div>
          <div style={s.formGroup}>
            <label style={s.formLabel}>Company Name</label>
            <input name="companyName" value={form.companyName} onChange={handleChange} style={s.input}
              placeholder="e.g. Wilson Construction" />
          </div>
        </div>

        <div style={s.formGrid}>
          <div style={s.formGroup}>
            <label style={s.formLabel}>Location *</label>
            <input name="location" value={form.location} onChange={handleChange} style={s.input}
              placeholder="e.g. Manchester, UK" required />
          </div>
          <div style={s.formGroup}>
            <label style={s.formLabel}>Job Type</label>
            <select name="jobType" value={form.jobType} onChange={handleChange} style={s.input}>
              <option value="permanent">Permanent</option>
              <option value="temporary">Temporary</option>
              <option value="contract">Contract</option>
            </select>
          </div>
        </div>

        <div style={s.formGrid}>
          <div style={s.formGroup}>
            <label style={s.formLabel}>Sector</label>
            <select name="sector" value={form.sector} onChange={handleChange} style={s.input}>
              <option value="">Select sector...</option>
              <option>Commercial Construction</option>
              <option>Residential</option>
              <option>Infrastructure & Civil</option>
              <option>Industrial & Energy</option>
              <option>Facilities Management</option>
              <option>Specialist Trades</option>
            </select>
          </div>
          <div style={s.formGroup}>
            <label style={s.formLabel}>Trade / Role</label>
            <input name="trade" value={form.trade} onChange={handleChange} style={s.input}
              placeholder="e.g. Electrician, Bricklayer" />
          </div>
        </div>

        <div style={s.formGrid}>
          <div style={s.formGroup}>
            <label style={s.formLabel}>Salary Display</label>
            <input name="salary" value={form.salary} onChange={handleChange} style={s.input}
              placeholder="e.g. £35,000 - £45,000" />
          </div>
          <div style={s.formGroup}>
            <label style={s.formLabel}>Salary Type</label>
            <select name="salaryType" value={form.salaryType} onChange={handleChange} style={s.input}>
              <option value="annual">Annual</option>
              <option value="daily">Daily Rate</option>
              <option value="hourly">Hourly Rate</option>
            </select>
          </div>
        </div>

        <div style={s.formGrid}>
          <div style={s.formGroup}>
            <label style={s.formLabel}>Min Salary (£)</label>
            <input name="salaryMin" type="number" value={form.salaryMin} onChange={handleChange}
              style={s.input} placeholder="e.g. 35000" />
          </div>
          <div style={s.formGroup}>
            <label style={s.formLabel}>Max Salary (£)</label>
            <input name="salaryMax" type="number" value={form.salaryMax} onChange={handleChange}
              style={s.input} placeholder="e.g. 45000" />
          </div>
        </div>

        <div style={s.formGrid}>
          <div style={s.formGroup}>
            <label style={s.formLabel}>Experience Level</label>
            <select name="experienceLevel" value={form.experienceLevel} onChange={handleChange} style={s.input}>
              <option value="">Select...</option>
              <option>Entry Level</option>
              <option>Junior</option>
              <option>Mid Level</option>
              <option>Senior</option>
              <option>Lead / Manager</option>
              <option>Director</option>
            </select>
          </div>
          <div style={s.formGroup}>
            <label style={s.formLabel}>Duration</label>
            <input name="duration" value={form.duration} onChange={handleChange} style={s.input}
              placeholder="e.g. 6 months, Ongoing" />
          </div>
        </div>

        <div style={s.formGrid}>
          <div style={s.formGroup}>
            <label style={s.formLabel}>Start Date</label>
            <input name="startDate" type="date" value={form.startDate} onChange={handleChange} style={s.input} />
          </div>
          <div style={s.formGroup}>
            <label style={s.formLabel}>Qualifications</label>
            <input name="qualifications" value={form.qualifications} onChange={handleChange} style={s.input}
              placeholder="e.g. NVQ Level 3, SMSTS" />
          </div>
        </div>

        <div style={s.formGroup}>
          <label style={s.formLabel}>Job Description *</label>
          <textarea name="description" value={form.description} onChange={handleChange}
            style={{ ...s.input, minHeight: '180px', resize: 'vertical' }}
            placeholder="Full job description, responsibilities, requirements..." required />
        </div>

        <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', marginBottom: '20px' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
            <input type="checkbox" name="cscsRequired" checked={form.cscsRequired} onChange={handleChange} />
            <span style={{ fontSize: '14px' }}>CSCS Card Required</span>
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
            <input type="checkbox" name="isFeatured" checked={form.isFeatured} onChange={handleChange} />
            <span style={{ fontSize: '14px' }}>⭐ Featured Job</span>
          </label>
        </div>

        <div style={s.formGrid}>
          <div style={s.formGroup}>
            <label style={s.formLabel}>Status</label>
            <select name="status" value={form.status} onChange={handleChange} style={s.input}>
              <option value="active">Active (Published)</option>
              <option value="draft">Draft</option>
              <option value="closed">Closed</option>
            </select>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
          <button type="submit" disabled={saving} style={s.primaryBtn}>
            {saving ? 'Saving...' : (job ? 'Update Job' : 'Publish Job')}
          </button>
          {onCancel && <button type="button" onClick={onCancel} style={s.secondaryBtn}>Cancel</button>}
        </div>
      </form>
    </div>
  );
}

// ============================== AUDIT LOGS VIEW ==============================
function AuditLogsView({ logs, total, page, setPage }) {
  return (
    <div>
      <h2 style={s.pageTitle}>Audit Logs ({total})</h2>
      <div style={s.table}>
        <div style={s.tableHeader}>
          <span style={{ flex: 1 }}>Time</span>
          <span style={{ flex: 1.5 }}>User</span>
          <span style={{ flex: 1 }}>Action</span>
          <span style={{ flex: 1 }}>Entity</span>
          <span style={{ flex: 2 }}>Details</span>
        </div>
        {logs.map(log => (
          <div key={log.id} style={s.tableRow}>
            <span style={{ flex: 1, fontSize: '13px', color: '#6c757d' }}>
              {new Date(log.createdAt).toLocaleString('en-GB')}
            </span>
            <span style={{ flex: 1.5, fontSize: '13px' }}>{log.userName || log.userEmail}</span>
            <span style={{ flex: 1 }}>
              <span style={{ ...s.typeBadge, background: log.action.includes('DELETE') ? '#dc3545' : log.action.includes('CREATE') ? '#28a745' : '#17a2b8' }}>
                {log.action}
              </span>
            </span>
            <span style={{ flex: 1, fontSize: '13px', color: '#6c757d' }}>
              {log.entityType}{log.entityId ? `:${log.entityId.slice(0, 8)}` : ''}
            </span>
            <span style={{ flex: 2, fontSize: '12px', color: '#6c757d', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {log.details ? (typeof log.details === 'string' ? log.details : JSON.stringify(log.details)).slice(0, 80) : '—'}
            </span>
          </div>
        ))}
      </div>
      {total > 30 && (
        <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', marginTop: '20px' }}>
          <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} style={s.pageBtn}>← Prev</button>
          <span style={{ padding: '8px', color: '#6c757d' }}>Page {page}</span>
          <button disabled={page >= Math.ceil(total / 30)} onClick={() => setPage(p => p + 1)} style={s.pageBtn}>Next →</button>
        </div>
      )}
    </div>
  );
}

// ============================== SETTINGS VIEW ==============================
function SettingsView({ users, total, page, setPage, userStats, onRefresh, flash }) {
  const [activeTab, setActiveTab] = useState('staff'); // 'staff' or 'candidates'
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [form, setForm] = useState({ email: '', firstName: '', lastName: '', password: '', role: 'operator', status: 'active' });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  // Filter users based on active tab
  const filteredUsers = users.filter(u => 
    activeTab === 'staff' ? ['admin', 'operator'].includes(u.role) : u.role === 'candidate'
  );
  const filteredTotal = filteredUsers.length;

  const resetForm = () => {
    const defaultRole = activeTab === 'staff' ? 'operator' : 'candidate';
    setForm({ email: '', firstName: '', lastName: '', password: '', role: defaultRole, status: 'active' });
    setEditingUser(null);
    setShowForm(false);
    setError('');
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setForm({
      email: user.email,
      firstName: user.firstName,
      lastName: user.lastName,
      password: '',
      role: user.role,
      status: user.status,
    });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    try {
      const url = editingUser ? `${API}/admin/users/${editingUser.id}` : `${API}/admin/users`;
      const method = editingUser ? 'PUT' : 'POST';
      const body = editingUser
        ? { firstName: form.firstName, lastName: form.lastName, role: form.role, status: form.status }
        : form;

      const res = await fetch(url, {
        method,
        headers: authHeaders(),
        body: JSON.stringify(body),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error);

      flash(editingUser ? 'User updated successfully' : 'User created successfully');
      resetForm();
      onRefresh();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (userId, email) => {
    if (!window.confirm(`Delete user ${email}? This action cannot be undone.`)) return;

    try {
      const res = await fetch(`${API}/admin/users/${userId}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error);
      }

      flash('User deleted successfully');
      onRefresh();
    } catch (err) {
      flash(err.message);
    }
  };

  const handleToggleStatus = async (userId, currentStatus) => {
    const newStatus = currentStatus === 'active' ? 'inactive' : 'active';

    try {
      const res = await fetch(`${API}/admin/users/${userId}`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ status: newStatus }),
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error);
      }

      flash(`User ${newStatus === 'active' ? 'activated' : 'deactivated'}`);
      onRefresh();
    } catch (err) {
      flash(err.message);
    }
  };

  return (
    <div>
      <h2 style={s.pageTitle}>User Management</h2>

      {/* Tab Navigation */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '25px', borderBottom: '2px solid #e9ecef' }}>
        <button
          onClick={() => { setActiveTab('staff'); resetForm(); setShowForm(false); }}
          style={{
            padding: '12px 24px',
            background: activeTab === 'staff' ? '#1a2f4d' : 'transparent',
            color: activeTab === 'staff' ? '#fff' : '#6c757d',
            border: 'none',
            borderBottom: activeTab === 'staff' ? '3px solid #ff6b35' : '3px solid transparent',
            cursor: 'pointer',
            fontWeight: 600,
            fontSize: '14px',
            transition: 'all 0.3s',
          }}
        >
          👥 Staff Management
        </button>
        <button
          onClick={() => { setActiveTab('candidates'); resetForm(); setShowForm(false); }}
          style={{
            padding: '12px 24px',
            background: activeTab === 'candidates' ? '#1a2f4d' : 'transparent',
            color: activeTab === 'candidates' ? '#fff' : '#6c757d',
            border: 'none',
            borderBottom: activeTab === 'candidates' ? '3px solid #ff6b35' : '3px solid transparent',
            cursor: 'pointer',
            fontWeight: 600,
            fontSize: '14px',
            transition: 'all 0.3s',
          }}
        >
          👤 Candidate Management
        </button>
      </div>

      {/* User Statistics */}
      {userStats && (
        <div style={{ marginBottom: '30px' }}>
          {activeTab === 'staff' && (
            <div style={{ ...s.statsGrid, gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))' }}>
              <div style={{ ...s.statCard, borderTopColor: '#ff6b35' }}>
                <div style={{ fontSize: '28px', fontWeight: 800, color: '#ff6b35' }}>{userStats.operators || 0}</div>
                <div style={{ fontSize: '13px', color: '#6c757d', marginTop: '5px' }}>Operators</div>
              </div>
              <div style={{ ...s.statCard, borderTopColor: '#1a2f4d' }}>
                <div style={{ fontSize: '28px', fontWeight: 800, color: '#1a2f4d' }}>{userStats.admins || 0}</div>
                <div style={{ fontSize: '13px', color: '#6c757d', marginTop: '5px' }}>Admins</div>
              </div>
              <div style={{ ...s.statCard, borderTopColor: '#343a40' }}>
                <div style={{ fontSize: '28px', fontWeight: 800, color: '#343a40' }}>{(userStats.operators || 0) + (userStats.admins || 0)}</div>
                <div style={{ fontSize: '13px', color: '#6c757d', marginTop: '5px' }}>Total Staff</div>
              </div>
            </div>
          )}
          {activeTab === 'candidates' && (
            <div style={{ ...s.statsGrid, gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))' }}>
              <div style={{ ...s.statCard, borderTopColor: '#17a2b8' }}>
                <div style={{ fontSize: '28px', fontWeight: 800, color: '#17a2b8' }}>{userStats.candidates || 0}</div>
                <div style={{ fontSize: '13px', color: '#6c757d', marginTop: '5px' }}>Total Candidates</div>
              </div>
              <div style={{ ...s.statCard, borderTopColor: '#28a745' }}>
                <div style={{ fontSize: '28px', fontWeight: 800, color: '#28a745' }}>{userStats.activeUsers || 0}</div>
                <div style={{ fontSize: '13px', color: '#6c757d', marginTop: '5px' }}>Active Users</div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Add/Edit User Form */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h3 style={{ margin: 0, fontSize: '20px', color: '#1a2f4d' }}>
          {activeTab === 'staff' ? `Staff Members (${filteredTotal})` : `Candidates (${filteredTotal})`}
        </h3>
        <button onClick={() => { resetForm(); setShowForm(!showForm); }} style={s.primaryBtn}>
          {showForm ? 'Cancel' : `➕ Add ${activeTab === 'staff' ? 'Staff' : 'Candidate'}`}
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} style={{ ...s.formCard, marginBottom: '20px' }}>
          <h4 style={{ marginTop: 0, color: '#1a2f4d' }}>
            {editingUser ? 'Edit User' : `Create New ${activeTab === 'staff' ? 'Staff Member' : 'Candidate'}`}
          </h4>
          {error && <div style={{ color: '#d33', marginBottom: '10px', padding: '10px', background: '#fee', borderRadius: '6px' }}>{error}</div>}

          <div style={s.formGrid}>
            <div style={s.formGroup}>
              <label style={s.formLabel}>First Name *</label>
              <input value={form.firstName} onChange={(e) => setForm(f => ({ ...f, firstName: e.target.value }))}
                style={s.input} required />
            </div>
            <div style={s.formGroup}>
              <label style={s.formLabel}>Last Name *</label>
              <input value={form.lastName} onChange={(e) => setForm(f => ({ ...f, lastName: e.target.value }))}
                style={s.input} required />
            </div>
          </div>

          <div style={s.formGrid}>
            <div style={s.formGroup}>
              <label style={s.formLabel}>Email *</label>
              <input type="email" value={form.email} onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))}
                style={s.input} required disabled={editingUser} />
            </div>
            {!editingUser && (
              <div style={s.formGroup}>
                <label style={s.formLabel}>Password *</label>
                <input type="password" value={form.password} onChange={(e) => setForm(f => ({ ...f, password: e.target.value }))}
                  style={s.input} required placeholder="Min 8 chars, 1 uppercase, 1 number" />
              </div>
            )}
          </div>

          <div style={s.formGrid}>
            <div style={s.formGroup}>
              <label style={s.formLabel}>Role *</label>
              <select value={form.role} onChange={(e) => setForm(f => ({ ...f, role: e.target.value }))} style={s.input}>
                {activeTab === 'staff' ? (
                  <>
                    <option value="operator">Operator</option>
                    <option value="admin">Admin</option>
                  </>
                ) : (
                  <option value="candidate">Candidate</option>
                )}
              </select>
            </div>
            <div style={s.formGroup}>
              <label style={s.formLabel}>Status *</label>
              <select value={form.status} onChange={(e) => setForm(f => ({ ...f, status: e.target.value }))} style={s.input}>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '10px' }}>
            <button type="submit" disabled={saving} style={s.primaryBtn}>
              {saving ? 'Saving...' : (editingUser ? 'Update User' : 'Create User')}
            </button>
            {editingUser && (
              <button type="button" onClick={resetForm} style={s.secondaryBtn}>Cancel</button>
            )}
          </div>
        </form>
      )}

      {/* Users Table */}
      <div style={s.table}>
        <div style={s.tableHeader}>
          <span style={{ flex: 2 }}>Name</span>
          <span style={{ flex: 2 }}>Email</span>
          <span style={{ flex: 1 }}>Role</span>
          <span style={{ flex: 1 }}>Status</span>
          <span style={{ flex: 1 }}>Last Login</span>
          <span style={{ flex: 1.5, textAlign: 'right' }}>Actions</span>
        </div>
        {filteredUsers.length === 0 && (
          <div style={{ padding: '20px', color: '#6c757d', textAlign: 'center' }}>
            No {activeTab === 'staff' ? 'staff members' : 'candidates'} found
          </div>
        )}
        {filteredUsers.map(usr => (
          <div key={usr.id} style={s.tableRow}>
            <span style={{ flex: 2, fontWeight: 600 }}>{usr.firstName} {usr.lastName}</span>
            <span style={{ flex: 2, color: '#6c757d', fontSize: '13px' }}>{usr.email}</span>
            <span style={{ flex: 1 }}>
              <span style={{
                ...s.typeBadge,
                background: usr.role === 'admin' ? '#1a2f4d' : usr.role === 'operator' ? '#ff6b35' : usr.role === 'recruiter' ? '#6f42c1' : '#17a2b8'
              }}>
                {usr.role}
              </span>
            </span>
            <span style={{ flex: 1 }}>
              <span style={{
                ...s.statusBadge,
                background: usr.status === 'active' ? '#d4edda' : '#f8d7da',
                color: usr.status === 'active' ? '#155724' : '#721c24'
              }}>
                {usr.status}
              </span>
            </span>
            <span style={{ flex: 1, color: '#6c757d', fontSize: '13px' }}>
              {usr.lastLoginAt ? new Date(usr.lastLoginAt).toLocaleDateString('en-GB') : 'Never'}
            </span>
            <span style={{ flex: 1.5, textAlign: 'right', display: 'flex', gap: '5px', justifyContent: 'flex-end' }}>
              <button onClick={() => handleEdit(usr)} style={{ ...s.smallBtn, background: '#e3f2fd', color: '#1976d2' }}>
                ✏️ Edit
              </button>
              <button onClick={() => handleToggleStatus(usr.id, usr.status)}
                style={{ ...s.smallBtn, background: usr.status === 'active' ? '#fff3cd' : '#d4edda' }}>
                {usr.status === 'active' ? '⏸️' : '▶️'}
              </button>
              <button onClick={() => handleDelete(usr.id, usr.email)}
                style={{ ...s.smallBtn, background: '#f8d7da', color: '#721c24' }}>
                🗑️
              </button>
            </span>
          </div>
        ))}
      </div>

      {/* Pagination */}
      {total > 30 && (
        <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', marginTop: '20px' }}>
          <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} style={s.pageBtn}>← Prev</button>
          <span style={{ padding: '8px', color: '#6c757d' }}>Page {page} of {Math.ceil(total / 30)}</span>
          <button disabled={page >= Math.ceil(total / 30)} onClick={() => setPage(p => p + 1)} style={s.pageBtn}>Next →</button>
        </div>
      )}
    </div>
  );
}

// ============================== STYLES ==============================
const s = {
  layout: { position: 'relative', minHeight: '100vh', fontFamily: "'Open Sans', sans-serif", width: '100vw', maxWidth: '100vw', overflow: 'hidden' },
  sidebar: {
    width: '260px', background: '#1a2f4d', color: '#fff', display: 'flex', flexDirection: 'column',
    position: 'fixed', top: 0, left: 0, bottom: 0, zIndex: 1000,
  },
  sidebarHeader: {
    padding: '20px', display: 'flex', alignItems: 'center', gap: '12px',
    borderBottom: '1px solid rgba(255,255,255,0.1)',
  },
  logo: {
    width: '40px', height: '40px', background: '#ff6b35', borderRadius: '8px',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '20px', fontWeight: 800, fontFamily: 'Montserrat, sans-serif', flexShrink: 0,
  },
  brandName: { fontWeight: 700, fontSize: '16px', fontFamily: 'Montserrat, sans-serif' },
  brandSub: { fontSize: '11px', color: '#94a3b8', letterSpacing: '1px' },
  nav: { flex: 1, padding: '15px 10px', display: 'flex', flexDirection: 'column', gap: '4px' },
  navBtn: {
    background: 'transparent', border: 'none', color: '#cbd5e1', padding: '12px 15px',
    borderRadius: '8px', textAlign: 'left', cursor: 'pointer', fontSize: '14px', fontWeight: 500,
    transition: 'all 0.2s',
  },
  navBtnActive: { background: 'rgba(255,107,53,0.2)', color: '#ff6b35' },
  submenu: {
    marginLeft: '10px', marginTop: '4px', marginBottom: '4px', paddingLeft: '10px',
    borderLeft: '2px solid rgba(255,255,255,0.1)',
  },
  submenuBtn: {
    background: 'transparent', border: 'none', color: '#94a3b8', padding: '10px 15px',
    borderRadius: '6px', textAlign: 'left', cursor: 'pointer', fontSize: '13px', fontWeight: 400,
    transition: 'all 0.2s', width: '100%', display: 'block',
  },
  submenuBtnActive: { background: 'rgba(255,107,53,0.15)', color: '#ff6b35', fontWeight: 500 },
  sidebarFooter: {
    padding: '15px 20px', borderTop: '1px solid rgba(255,255,255,0.1)',
  },
  logoutBtn: {
    background: 'rgba(255,255,255,0.1)', border: 'none', color: '#fff', padding: '8px 16px',
    borderRadius: '6px', cursor: 'pointer', fontSize: '13px', width: '100%',
  },
  main: {
    marginLeft: '260px', padding: '30px', background: '#f8f9fa', minHeight: '100vh',
    width: 'calc(100vw - 260px)', boxSizing: 'border-box',
  },
  pageTitle: { fontSize: '24px', fontWeight: 700, color: '#1a2f4d', margin: '0 0 20px', fontFamily: 'Montserrat, sans-serif' },
  statsGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '15px',
  },
  statCard: {
    background: '#fff', padding: '25px', borderRadius: '10px', textAlign: 'center',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)', borderTop: '4px solid #ccc',
  },
  table: { background: '#fff', borderRadius: '10px', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' },
  tableHeader: {
    display: 'flex', padding: '15px 20px', background: '#f1f3f5', fontWeight: 700,
    fontSize: '13px', color: '#6c757d', textTransform: 'uppercase', letterSpacing: '0.5px',
  },
  tableRow: {
    display: 'flex', padding: '15px 20px', borderBottom: '1px solid #f1f3f5',
    alignItems: 'center', fontSize: '14px',
  },
  input: {
    width: '100%', padding: '10px 14px', border: '2px solid #e9ecef', borderRadius: '8px',
    fontSize: '14px', outline: 'none', boxSizing: 'border-box',
  },
  formCard: { background: '#fff', padding: '30px', borderRadius: '10px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' },
  formGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' },
  formGroup: { display: 'flex', flexDirection: 'column' },
  formLabel: { fontSize: '13px', fontWeight: 600, color: '#1a2f4d', marginBottom: '5px' },
  primaryBtn: {
    padding: '12px 24px', background: '#ff6b35', color: '#fff', border: 'none',
    borderRadius: '8px', fontSize: '14px', fontWeight: 700, cursor: 'pointer',
    fontFamily: 'Montserrat, sans-serif',
  },
  secondaryBtn: {
    padding: '12px 24px', background: '#e9ecef', color: '#1a2f4d', border: 'none',
    borderRadius: '8px', fontSize: '14px', fontWeight: 600, cursor: 'pointer',
  },
  smallBtn: {
    padding: '6px 10px', background: '#f1f3f5', border: 'none', borderRadius: '6px',
    cursor: 'pointer', fontSize: '13px',
  },
  pageBtn: {
    padding: '8px 16px', background: '#e9ecef', border: 'none', borderRadius: '6px',
    cursor: 'pointer', fontSize: '13px',
  },
  typeBadge: {
    display: 'inline-block', padding: '3px 10px', borderRadius: '20px',
    fontSize: '12px', fontWeight: 600, color: '#fff',
  },
  statusBadge: {
    display: 'inline-block', padding: '3px 10px', borderRadius: '20px',
    fontSize: '12px', fontWeight: 600,
  },
  featuredBadge: {
    display: 'inline-block', marginLeft: '6px', color: '#ff6b35', fontSize: '14px',
  },
  flash: {
    background: '#d4edda', color: '#155724', padding: '12px 20px', borderRadius: '8px',
    marginBottom: '20px', fontWeight: 600, fontSize: '14px',
  },
};
